import { fetchShopDeliveryInfo } from './shopFreeShipping';
import { getSubtotalForFreeShippingEligible } from './cartTotals';
import {
  cartLineNeedsShippingQuote,
  ensureCartLineShippingFields,
} from './cartShippingEligibility';

export const FREE_SHIP_FALLBACK = 200;

/**
 * Reads the store’s free-shipping floor from GET /orders/get-shipping-cost (optionally ?businessId=).
 * Falls back to FREE_SHIP_FALLBACK when the request fails.
 */
export async function getFreeShippingThresholdFromApi({ apiBaseV1, businessId }) {
  const d = await fetchShopDeliveryInfo({ apiBaseV1, businessId });
  return d.freeThreshold;
}

/**
 * Server-side shop shipping quote (aligns with POST /api/v1/orders/quote-shipping).
 */
export async function quoteShippingFromCart({
  apiBaseV1,
  businessId,
  cartItems,
  deliveryMode = 'ship',
  shippingAddress,
  selectedShippingOptionId,
}) {
  const lines = (cartItems || []).map(ensureCartLineShippingFields);
  const hasPhysical = lines.some((item) => cartLineNeedsShippingQuote(item));
  if (!hasPhysical) {
    return {
      shippingCost: 0,
      freeThreshold: FREE_SHIP_FALLBACK,
      showFreeShippingPromo: true,
      useDefaultFreeShippingThreshold: true,
      subtotalFreeShippingEnabled: true,
      pickupEligible: false,
      breakdown: [],
      requiresManualQuote: false,
      shippingOptions: [],
      appliedShippingOptionId: null,
    };
  }
  const orderSubtotalForThreshold = getSubtotalForFreeShippingEligible(lines);
  const productItems = lines
    .filter((i) => !(i.type === 'event' || i.eventId))
    .map((i) => ({
      productId: i.productId || i.id,
      quantity: i.quantity || 1,
      variantId: i.variantId || undefined,
    }))
    .filter((row) => Boolean(row.productId));
  if (!businessId || productItems.length === 0) {
    const d = await fetchShopDeliveryInfo({ apiBaseV1, businessId: (businessId || '').trim() });
    return {
      shippingCost: 10,
      freeThreshold: d.freeThreshold,
      showFreeShippingPromo: d.showFreeShippingPromo,
      useDefaultFreeShippingThreshold: d.useDefaultFreeShippingThreshold,
      subtotalFreeShippingEnabled: d.subtotalFreeShippingEnabled !== false,
      pickupEligible: false,
      breakdown: [],
      requiresManualQuote: false,
      shippingOptions: [],
      appliedShippingOptionId: null,
    };
  }
  const res = await fetch(`${apiBaseV1}/orders/quote-shipping`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      businessId,
      deliveryMode,
      orderSubtotal: orderSubtotalForThreshold,
      items: productItems,
      ...(selectedShippingOptionId ? { selectedShippingOptionId } : {}),
      shippingAddress:
        deliveryMode === 'pickup'
          ? undefined
          : {
              country: (shippingAddress && shippingAddress.country) || 'AU',
              state: shippingAddress && shippingAddress.state,
              postalCode: shippingAddress && shippingAddress.postalCode,
              line1: shippingAddress && shippingAddress.street,
              city: shippingAddress && shippingAddress.city,
            },
    }),
  });
  const data = await res.json();
  if (!res.ok) {
    const d = await fetchShopDeliveryInfo({ apiBaseV1, businessId: (businessId || '').trim() });
    return {
      shippingCost: 10,
      freeThreshold: d.freeThreshold,
      showFreeShippingPromo: d.showFreeShippingPromo,
      useDefaultFreeShippingThreshold: d.useDefaultFreeShippingThreshold,
      subtotalFreeShippingEnabled: d.subtotalFreeShippingEnabled !== false,
      pickupEligible: false,
      breakdown: [],
      requiresManualQuote: false,
      shippingOptions: [],
      appliedShippingOptionId: null,
    };
  }
  return {
    shippingCost: Number(data.shippingCost) || 0,
    freeThreshold: Number(data.freeThreshold) || FREE_SHIP_FALLBACK,
    pickupEligible: !!data.pickupEligible,
    showFreeShippingPromo: data.showFreeShippingPromo !== false,
    useDefaultFreeShippingThreshold: data.useDefaultFreeShippingThreshold !== false,
    subtotalFreeShippingEnabled: data.subtotalFreeShippingEnabled !== false,
    breakdown: Array.isArray(data.breakdown) ? data.breakdown : [],
    requiresManualQuote: data.requiresManualQuote === true,
    shippingOptions: Array.isArray(data.shippingOptions) ? data.shippingOptions : [],
    appliedShippingOptionId: data.appliedShippingOptionId || null,
  };
}
